import React from 'react';
import cn from 'classnames';
import { Form, Localization, Modal, usePostMessageContext } from 'connex-cds';
import { find, isEmpty, omit } from 'lodash';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { cacheItem } from '../../../../../util/cache';
import { useCompanySetup, useProductSetup, useVehicleSetup, useVehicleTypeSetup } from '../MasterDataProvider';
import { useTicketContext } from '../TicketContext';
import { useCertificationLoss } from '../useCertificationLoss';
import { UnFinalize } from './modal/UnFinalize';
import { ResponsiveWizardSteps } from './wizard-steps/ResponsiveWizardSteps';
import { STEP_STATUS } from './wizard-steps/WizardSteps';
import pkg from '../../../../../../package.json';
import { useProbeListener } from '../useProbeListener';
import { useAutomaticTicketSubmission } from '../useAutomaticTicketSubmission';
import { useSetPricing } from '../useSetPricing';
import { useGetOrderRecipients } from '../../../../../query-hooks/order-recipients';
import { cleanupLineItems } from '../../../../../util/cleanupLineItems';
import { mobileTicket } from '../../../../../api';
import { Logger } from '../../../../../util/log/logger';
import steps from './steps';
import style from './style';

const Styled = styled.div`
  ${style}
`;

const ACCEPTED_REJECTED = ['ACCEPTED', 'REJECTED'];

export const Wizard = () => {
  const { stepId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    ticket,
    finalized,
    submitted,
    setFinalized,
    paymentMethod,
    refreshTicket,
    isStandAloneTicket,
    loading: isTicketContextLoading,
  } = useTicketContext();
  const { errors, setErrors, values, setFieldValue } = Form.useFormContext();

  const { sendMessage } = usePostMessageContext();
  const [currentStep, setCurrentStep] = React.useState('ticketDetail');
  const translateMessage = Localization.useTranslateMessage();
  const [stepTouched, setStepTouched] = React.useState({ ticketDetail: true });

  const companySetup = useCompanySetup();
  const vehicleSetup = useVehicleSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const productSetup = useProductSetup();

  const { entityRef } = useParams();

  const orderRecipients = useGetOrderRecipients(ticket?.orderRef, ticket?.crn, companySetup);
  const { setPricing } = useSetPricing();

  useProbeListener();
  useCertificationLoss();
  const { submitTicket } = useAutomaticTicketSubmission();

  const vehicleType = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    const targetVehicleType = find(vehicleTypeSetup.data, {
      crn: targetVehicle?.vehicleType?.vehicleTypeRef,
    });
    return targetVehicleType?.id;
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const isEnablePaymentAndCostWorkflows = React.useMemo(
    () => companySetup?.data?.isEnablePaymentAndCostWorkflows ?? false,
    [companySetup?.data?.isEnablePaymentAndCostWorkflows]
  );

  const isSlumpRackWaterAtPlant = React.useMemo(
    () => companySetup?.data?.isSlumpRackWaterAtPlant ?? false,
    [companySetup?.data?.isSlumpRackWaterAtPlant]
  );

  const vehicleTypeData = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    return find(vehicleTypeSetup.data, {
      crn: targetVehicle?.vehicleType?.vehicleTypeRef,
    });
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const isMaterialServiceMandatory = React.useMemo(() => {
    const hasTicketDriverReview = ticket?.lineItems?.some(
      item => !item?.isPrimary && !item?.isSplitLoad && item?.requiresDriverReview
    );
    const hasSetupMandatory = productSetup?.data?.some?.(
      product =>
        product.isDriverSellable &&
        product.status === 'ACTIVE' &&
        (companySetup?.data?.isMulticountry === true
          ? product?.countries?.includes(ticket?.origin?.address?.countryCode) || !product?.countries?.length
          : true) &&
        (product?.vehicleTypes?.includes(vehicleType) || !product?.vehicleTypes?.length) &&
        product?.mobileTicket?.mandatory === true
    );
    return hasTicketDriverReview || hasSetupMandatory;
  }, [
    companySetup?.data?.isMulticountry,
    productSetup?.data,
    ticket?.lineItems,
    ticket?.origin?.address?.countryCode,
    vehicleType,
  ]);

  const translatedStepsStatus = React.useMemo(() => {
    let filteredSteps = steps;

    const errorsCleaned = cleanupLineItems(errors, values);

    if (companySetup?.data?.isWaterInWorkflow === false) {
      filteredSteps = steps.filter(step => step.id !== 'water');
    } else if (vehicleTypeData) {
      if (companySetup?.data?.isWaterInWorkflow !== false && vehicleTypeData?.isWaterInWorkflow !== true) {
        filteredSteps = steps.filter(step => step.id !== 'water');
      }
    }

    if (companySetup?.data?.isReturnedMaterialInWorkflow === false || ticket?.customData?.ticketType === 'PMP') {
      filteredSteps = filteredSteps.filter(step => step.id !== 'returnedMaterial');
    } else if (vehicleTypeData) {
      if (
        companySetup?.data?.isReturnedMaterialInWorkflow !== false &&
        vehicleTypeData?.isReturnedMaterialInWorkflow !== true
      ) {
        filteredSteps = filteredSteps.filter(step => step.id !== 'returnedMaterial');
      }
    }
    if (companySetup?.data?.isMaterialServiceInWorkflow === false) {
      filteredSteps = filteredSteps.filter(step => step.id !== 'materialService');
    } else if (vehicleTypeData) {
      if (
        companySetup?.data?.isMaterialServiceInWorkflow !== false &&
        vehicleTypeData?.isMaterialServiceInWorkflow !== true
      ) {
        filteredSteps = filteredSteps.filter(step => step.id !== 'materialService');
      }
    }
    if (ticket?.customData?.ticketType !== 'PMP') {
      filteredSteps = filteredSteps.filter(step => step.id !== 'totalPumpedQuantity');
    }
    if (companySetup?.data?.isDotEnabled !== true) {
      filteredSteps = filteredSteps.filter(step => step.id !== 'dotWorkflow');
    }
    if (companySetup?.data?.isMaterialComplianceEnabled === false) {
      filteredSteps = filteredSteps.filter(step => step.id !== 'materialCompliance');
    } else if (vehicleTypeData) {
      if (
        companySetup?.data?.isMaterialComplianceEnabled !== false &&
        vehicleTypeData?.isMaterialComplianceEnabled !== true
      ) {
        filteredSteps = filteredSteps.filter(step => step.id !== 'materialCompliance');
      }
    }
    if (companySetup?.data?.isRiskAssessmentEnabled !== true) {
      filteredSteps = filteredSteps.filter(step => step.id !== 'riskAssessment');
    }
    return filteredSteps.map(step => {
      const paths = [].concat(step.path);

      let status = STEP_STATUS.DEFAULT;
      if (step.id === 'water') {
        if (
          values?.waterAddedEvents.some(item => item.suggestedToDriver) &&
          !stepTouched.water &&
          !!errorsCleaned.waterAddedEvents
        ) {
          status = STEP_STATUS.MANDATORY;
        }
      } else if (step.id === 'materialService') {
        if (
          isMaterialServiceMandatory &&
          !stepTouched.materialService &&
          vehicleTypeData?.isMaterialServiceInWorkflow !== false
        ) {
          status = STEP_STATUS.MANDATORY;
        }
      } else if (step.id === 'returnedMaterial') {
        if (
          companySetup?.data?.isReturnedMaterialMandatory === true &&
          !stepTouched.returnedMaterial &&
          vehicleTypeData?.isReturnedMaterialInWorkflow !== false &&
          ticket?.customData?.ticketType !== 'PMP'
        ) {
          status = STEP_STATUS.MANDATORY;
        }
      } else if (step.id === 'totalPumpedQuantity') {
        if (ticket?.customData?.ticketType === 'PMP') {
          status = STEP_STATUS.MANDATORY;
        }
      }
      if (paths.some(p => !!errorsCleaned[p]) && stepTouched[step.id]) {
        status = STEP_STATUS.ERROR;
      } else if (stepTouched[step.id]) {
        status = STEP_STATUS.OK;
      }
      return {
        ...step,
        title: translateMessage(step.id, { value: ticket?.id }),

        status,
      };
    });
  }, [
    companySetup?.data?.isMaterialComplianceEnabled,
    companySetup?.data?.isMaterialServiceInWorkflow,
    companySetup?.data?.isReturnedMaterialInWorkflow,
    companySetup?.data?.isReturnedMaterialMandatory,
    companySetup?.data?.isWaterInWorkflow,
    errors,
    isMaterialServiceMandatory,
    stepTouched,
    ticket?.customData?.ticketType,
    ticket?.id,
    translateMessage,
    values,
    vehicleTypeData,
  ]);

  const translatedSteps = React.useMemo(() => {
    const ticketEvents = Object.keys(ticket?.ticketEvents ?? {});
    //Log for France, Germany, and BENE for debugging disabled finalize step issue
    const debugEntityRefs = new Set([
      'cf192077-6680-497b-91a4-2d8f3312d70a',
      '041b273d-2af4-4f8a-9197-b46d584dd74a',
      'dcee0e6b-6890-479d-82ba-d820ea43b298',
      '0c7fe85d-8298-48b7-9dec-015ab4109b80',
      '44834081-973b-45ad-83b4-f4ca38af53ea',
      '51f55611-3e74-48df-9437-99815d818522',
      'c04d9798-68d7-40ab-a814-88f8366fbaed',
    ]);

    return translatedStepsStatus.map(step => {
      let disabled = false;
      let loading = false;
      let hidden = false;
      let errorsToCheck = omit(errors, 'ticketEvents');

      if (step.id === 'water') {
        const steps = isSlumpRackWaterAtPlant
          ? ['LOADING_COMPLETE', 'TO_JOB', 'ARRIVE_JOB', 'READY_TO_PUMP', 'UNLOADING', 'START_PUMPING']
          : ['ARRIVE_JOB', 'READY_TO_PUMP', 'UNLOADING', 'START_PUMPING'];
        if (!isStandAloneTicket && !ticketEvents?.some(event => steps.includes(event))) {
          disabled = true;
        }
      } else if (step.id === 'totalPumpedQuantity') {
        if (
          !isStandAloneTicket &&
          !ticketEvents?.some(event => ['END_UNLOADING', 'PUMPING_COMPLETE'].includes(event))
        ) {
          disabled = true;
        }
      } else if (step.id === 'returnedMaterial') {
        if (
          (!isStandAloneTicket &&
            !ticketEvents?.some(event => ['ARRIVE_JOB', 'UNLOADING', 'END_UNLOADING', 'LEAVE_JOB'].includes(event))) ||
          (!ticketEvents?.some(event => ['UNLOADING', 'END_UNLOADING', 'PUMPING_COMPLETE'].includes(event)) &&
            !Boolean(companySetup?.data?.isRejectLoadsEnable))
        ) {
          disabled = true;
          Logger.debug(
            `Mobile Ticket - Wizard.js:245 returnedMaterial disabled ${JSON.stringify({
              ticketRef: ticket?.crn,
              ticketId: ticket?.id,
              step,
              isStandAloneTicket,
              fullTicketEvents: ticket?.ticketEvents,
              ticketEvents,
              isRejectLoadsEnable: companySetup?.data?.isRejectLoadsEnable,
            })}`
          );
        }
      } else if (step.id === 'paymentMethod') {
        if (!paymentMethod || !paymentMethod.isCOD || !isEnablePaymentAndCostWorkflows) {
          hidden = true;
        }
      } else if (step.id === 'finalize') {
        const isMaterialServiceNotInWorkflow = companySetup?.data?.isMaterialServiceInWorkflow !== true;
        const isVehicleTypeMaterialServiceNotTrue = vehicleTypeData?.isMaterialServiceInWorkflow !== true;
        const shouldOmitLineItemsByWorkflow = isMaterialServiceNotInWorkflow || isVehicleTypeMaterialServiceNotTrue;

        if (shouldOmitLineItemsByWorkflow) {
          errorsToCheck = omit(errors, 'ticketEvents', 'lineItems');
        }

        const itemsNotSuggestedByDriver = values?.lineItems?.reduce((acc, el, pos) => {
          if (!el?.suggestedToDriver) acc.push(`lineItems[${pos}]`);
          return acc;
        }, []);
        if (itemsNotSuggestedByDriver?.length) {
          errorsToCheck = omit(errors, 'ticketEvents', ...itemsNotSuggestedByDriver);

          const lineItemsErrors = errorsToCheck?.lineItems?.filter(Boolean);
          if (!lineItemsErrors?.length) {
            delete errorsToCheck.lineItems;
          }
          if (lineItemsErrors?.length) {
            errorsToCheck.lineItems = lineItemsErrors;
          }
        }

        const hasMandatoryOrErrorStatus = translatedStepsStatus?.some(
          s => s.status === 'mandatory' || s.status === 'error'
        );
        const originalCondition = !isEmpty(errorsToCheck) || hasMandatoryOrErrorStatus;
        const finishEvents = ticketEvents?.some(event =>
          ['UNLOADING', 'END_UNLOADING', 'PUMPING_COMPLETE'].includes(event)
        );

        if (
          !isStandAloneTicket &&
          !ticketEvents?.some(event => ['END_UNLOADING', 'PUMPING_COMPLETE'].includes(event))
        ) {
          disabled = true;
        }

        if (companySetup?.data?.rejectLoadStep === 'returnedMaterial' || !companySetup?.data?.rejectLoadStep) {
          if ((!isStandAloneTicket && !finishEvents) || originalCondition) {
            disabled = true;
          }
        } else if (
          (!isStandAloneTicket &&
            !ticketEvents?.some(event =>
              ['ARRIVE_JOB', 'UNLOADING', 'END_UNLOADING', 'LEAVE_JOB', 'PUMPING_COMPLETE'].includes(event)
            )) ||
          originalCondition
        ) {
          disabled = true;
        }

        if (debugEntityRefs?.has(entityRef)) {
          const freezedMessage = JSON.stringify({
            type: 'debug',
            ack: {
              ticketRef: ticket?.crn,
              ticketId: ticket?.id,
              step: {
                ...step,
                disabled,
                loading,
                hidden,
              },
              workflowChecks: {
                companyMaterialService: companySetup?.data?.isMaterialServiceInWorkflow,
                isMaterialServiceNotInWorkflow,
                vehicleMaterialService: vehicleTypeData?.isMaterialServiceInWorkflow,
                isVehicleTypeMaterialServiceNotTrue,
                shouldOmitLineItemsByWorkflow,
                companyWaterInWorkflow: companySetup?.data?.isWaterInWorkflow,
                vehicleWaterInWorkflow: vehicleTypeData?.isWaterInWorkflow,
              },
              itemsNotSuggestedByDriver,
              stepStatusCheck: { hasMandatoryOrErrorStatus, errorsToCheck },
              eventConditions: { finishEvents, originalCondition, rejectLoadStep: companySetup?.data?.rejectLoadStep },
              fullticketEvents: ticket?.ticketEvents,
              ticketEvents,
              errorsToCheck,
              valuesLineItems: values?.lineItems,
              translatedStepsStatus,
              originalErrors: errors,
              returnedMaterialDriverDidNotAdd: values?.driverDidNotAdd,
              workflowSummary: {
                totalSteps: translatedStepsStatus?.length,
                errorSteps: translatedStepsStatus?.filter(s => s.status === 'error')?.length,
                mandatorySteps: translatedStepsStatus?.filter(s => s.status === 'mandatory')?.length,
              },
            },
          });

          Logger.debug(`Mobile Ticket - Wizard.js:333 ${freezedMessage}`);
          sendMessage(freezedMessage);
        }
      }

      return {
        ...step,
        disabled,
        loading,
        hidden,
      };
    });
  }, [
    errors,
    ticket,
    isStandAloneTicket,
    values?.lineItems,
    translatedStepsStatus,
    isEnablePaymentAndCostWorkflows,
    isSlumpRackWaterAtPlant,
    sendMessage,
    companySetup,
    vehicleTypeData,
    paymentMethod,
    entityRef,
  ]);

  const isEarlyAcceptance = React.useMemo(() => {
    if (!companySetup?.data?.isEarlySignatureEnabled) {
      return false;
    }

    const searchParams = new URLSearchParams(location?.search);
    return searchParams.get('showEarlyAcceptance') ?? false;
  }, [location?.search, companySetup?.data?.isEarlySignatureEnabled]);

  const updateTicket = React.useCallback(async () => {
    try {
      const ticket = values;
      Logger.debug(`Mobile Ticket - Wizard.js:366 - updateTicket payload: ${JSON.stringify({ entityRef, ticket })}`);
      const result = await mobileTicket.updateTicket({ entityRef, ticket });
      Logger.debug(`Mobile Ticket - Wizard.js:366 - updateTicket Success: ${JSON.stringify(result)}`);
      return result;
    } catch (error) {
      Logger.error(`Mobile Ticket - Wizard.js:369 - updateTicket Error: ${error.message}`);
      return { error };
    }
  }, [entityRef, values]);

  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    cacheItem(`state`, JSON.stringify({ [ticket.crn]: values }));
  }, [ticket.crn, values]);

  React.useEffect(() => {
    if (orderRecipients?.data?.recipients) {
      const filteredOrderRecipients = orderRecipients?.data?.recipients?.filter(
        recipient => recipient?.type !== 'contact'
      );
      setFieldValue(`orderRecipients`, filteredOrderRecipients || []);
      if (orderRecipients?.data?.recipients?.length) setFieldValue('hasSentRecipients', true);
    }
  }, [orderRecipients?.data?.recipients, setFieldValue]);

  React.useEffect(() => {
    if (!paymentMethod || !isEnablePaymentAndCostWorkflows) {
      return;
    }

    setPricing();

    const step = translatedSteps.find(step => step.id === 'paymentMethod');
    if (!paymentMethod.isCOD) {
      step.hidden = true;
    } else if (paymentMethod.isCOD) {
      setFieldValue('isExpectedPaymentMethod', true);
      step.hidden = false;
    }
  }, [paymentMethod, isEnablePaymentAndCostWorkflows, setPricing, setFieldValue]);

  React.useEffect(() => {
    if (errors?.waterAddedEvents && !translatedStepsStatus.some(step => step.id === 'water')) {
      const waterAddedEventsErrors = omit(errors, 'waterAddedEvents');
      setErrors(waterAddedEventsErrors);
    }
  }, [translatedStepsStatus, errors, setErrors]);

  // Add a ref to track if submission has been attempted
  const submissionAttemptedRef = React.useRef(false);

  React.useEffect(() => {
    const ticketEvents = Object.keys(ticket?.ticketEvents);

    // Check if already attempted submission for this ticket
    if (submissionAttemptedRef.current) {
      return;
    }

    if (
      companySetup?.data?.isAutomaticTicketSubmissionEnable &&
      !ACCEPTED_REJECTED.includes(ticket?.sk?.customerStatus) &&
      (!ACCEPTED_REJECTED.includes(ticket?.customerStatus) ||
        !ACCEPTED_REJECTED.includes(ticket?.customerStatus?.statusCode))
    ) {
      if (
        companySetup?.data?.automaticStatusTrigger === 'begin_wash' &&
        ticketEvents?.some(event => ['WASHING', 'END_WASHING', 'LEAVE_JOB', 'ARRIVE_PLANT', 'IN_YARD'].includes(event))
      ) {
        submissionAttemptedRef.current = true;
        submitTicket();
      } else if (
        companySetup?.data?.automaticStatusTrigger === 'depart_job' &&
        ticketEvents?.some(event => ['LEAVE_JOB', 'ARRIVE_PLANT', 'IN_YARD'].includes(event))
      ) {
        submissionAttemptedRef.current = true;
        submitTicket();
      } else if (
        companySetup?.data?.automaticStatusTrigger === 'arrive_plant' &&
        ticketEvents?.some(event => ['ARRIVE_PLANT', 'IN_YARD'].includes(event))
      ) {
        submissionAttemptedRef.current = true;
        submitTicket();
      }
    }
  }, [
    ticket?.ticketEvents,
    ticket?.customerStatus,
    ticket?.sk?.customerStatus,
    companySetup?.data?.automaticStatusTrigger,
    companySetup?.data?.isAutomaticTicketSubmissionEnable,
  ]);

  // Reset the ref when ticket changes
  React.useEffect(() => {
    submissionAttemptedRef.current = false;
  }, [ticket?.crn]);

  React.useEffect(() => {
    if (finalized && stepId !== 'finalize') {
      setCurrentStep(stepId);
      setFinalized(false);

      return;
    }

    if (finalized || (!finalized && submitted)) {
      setCurrentStep('finalize');

      return;
    }

    setCurrentStep(stepId);
  }, [finalized, stepId, submitted]);

  React.useEffect(() => {
    const updateAndRefresh = async () => {
      try {
        const result = await updateTicket();
        if (result?.error || result === undefined || result === null || JSON.stringify(result) === '{}') {
          return;
        }
        await refreshTicket();
      } finally {
        setIsLoading(false);
      }
    };

    if (
      companySetup?.data?.isRealTimeUpdatesEnabled === true &&
      !ACCEPTED_REJECTED.includes(ticket?.sk?.customerStatus) &&
      (!ACCEPTED_REJECTED.includes(ticket?.customerStatus) ||
        !ACCEPTED_REJECTED.includes(ticket?.customerStatus?.statusCode))
    ) {
      setIsLoading(true);
      updateAndRefresh();
    }
  }, [currentStep]);

  const handleStepClick = React.useCallback(
    value => {
      setStepTouched(s => ({ ...s, [value]: true }));
      navigate(`../${value}`);
    },
    [navigate]
  );

  const { openModal, closeModal } = Modal.useModalContext();

  const handleBackClick = React.useCallback(() => {
    const backToDetails = () => {
      if (isEarlyAcceptance) {
        navigate(`../ticketDetail`);
      }
    };

    if (!companySetup?.data?.unFinalizePassword) {
      setFinalized(false);
      backToDetails();
      return;
    }

    const onOkay = password => {
      if (companySetup?.data?.unFinalizePassword === password) {
        closeModal();
        setFinalized(false);
        backToDetails();
      }
    };

    openModal({
      title: 'Modal',
      component: <UnFinalize onOkay={onOkay} onCancel={closeModal} />,
    });
  }, [closeModal, companySetup?.data, openModal, setFinalized, isEarlyAcceptance]);

  const currentComponent = React.useMemo(() => {
    const target = find(steps, { id: currentStep });
    return target?.component;
  }, [currentStep]);

  return (
    <Styled className={cn('wizard', { finalized })}>
      {!finalized && !submitted ? (
        <div className={cn('header-wizard')}>
          <div className={cn('steps')}>
            <ResponsiveWizardSteps
              steps={translatedSteps}
              currentStep={currentStep}
              onClick={handleStepClick}
              handleBackClick={handleBackClick}
            />
          </div>
        </div>
      ) : null}

      <div className={cn('step-component')}>
        {React.cloneElement(currentComponent, { handleBackClick, isLoading: isLoading || isTicketContextLoading })}
      </div>

      <div className={cn('env')}>
        {window?.location?.hostname} v{pkg.version}
      </div>
    </Styled>
  );
};
